import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Icon } from "@/components";
import { useForm, useMeta } from "@/hooks";
import {
    Button,
    Callout,
    Heading,
    LoadingProgress,
    Message,
    Panel,
    Select,
    Text,
} from "@/ui";
import { categoryType } from "@/config";
import styles from "./AppsConfigForm.module.scss";
import generalsStyles from "./GeneralsForm.module.scss";

const SearchIcon = () => <Icon icon="search" />;

const LINE_LIMIT = 3;

/** Description with 3-line clamp + DS tertiary toggle (same behavior as legacy `Paragraph`). */
const AppDescription = ({ text }) => {
    const textRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [canToggle, setCanToggle] = useState(false);

    useEffect(() => {
        const node = textRef.current;

        if (!node) return undefined;

        const checkOverflow = () => {
            const lineHeight =
                parseInt(window.getComputedStyle(node).lineHeight, 10) || 16;

            setCanToggle(node.scrollHeight > lineHeight * LINE_LIMIT + 1);
        };

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(checkOverflow);
        });

        observer.observe(node);
        checkOverflow();

        return () => observer.disconnect();
    }, [text]);

    if (!text) {
        return null;
    }

    return (
        <div className={styles.cardDescription}>
            <p
                className={classNames(styles.cardDescriptionText, {
                    [styles.cardDescriptionExpanded]: isExpanded,
                })}
                ref={textRef}
            >
                {text}
            </p>
            {canToggle ? (
                <button
                    className={styles.cardDescriptionToggle}
                    onClick={() => setIsExpanded(current => !current)}
                    type="button"
                >
                    {isExpanded ? "less..." : "...more"}
                </button>
            ) : null}
        </div>
    );
};

const AppsConfigForm = ({
    apps,
    loading,
    installApp,
    unistallApp,
    loadingAction,
    setLoadingAction,
}) => {
    const { user } = useMeta();
    const { inputs, selectChange } = useForm();

    const categoriesOptions = [
        { label: "All", value: "ALL" },
        ...categoryType.map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        })),
    ];

    const selectedCategory = inputs?.category || "ALL";

    const filteredApps =
        selectedCategory && selectedCategory !== "ALL"
            ? apps.filter(app => app.app.category === selectedCategory)
            : apps;

    const handleAction = (option, isInstalled) => {
        setLoadingAction(option.variables.input.id);

        if (isInstalled) {
            unistallApp(option);
        } else {
            installApp(option);
        }
    };

    return (
        <Panel
            className={classNames(generalsStyles.root, styles.root)}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Apps</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Supported third party integrations
                        </Text>
                    </div>
                    <div className={styles.headerActions}>
                        <Select
                            className={styles.categorySelect}
                            label="Filter by category"
                            onValueChange={value =>
                                selectChange({ value }, "category")
                            }
                            options={categoriesOptions}
                            value={selectedCategory}
                        />
                    </div>
                </div>
            }
            showHeaderDivider
        >
            {loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <div className={styles.body}>
                    <Callout variant="info" width="full">
                        Be sure to reach out to your account representative
                        before installing an app.
                    </Callout>

                    <Heading className={styles.sectionTitle} level={5}>
                        All Apps
                    </Heading>

                    {filteredApps?.length > 0 ? (
                        <div className={styles.grid}>
                            {filteredApps.map(item => {
                                const { app, icon, description, isInstalled } =
                                    item;

                                const option = {
                                    variables: {
                                        account_id: user.accountId,
                                        store_id: user.storeId,
                                        input: {
                                            id: app.id,
                                            name: app.name,
                                            handler: app.handler,
                                            type: app.type,
                                            category: app.category,
                                            created_at: app.created_at,
                                            updated_at: app.updated_at,
                                            status: app.status,
                                        },
                                    },
                                };

                                const showLoading = app?.id === loadingAction;

                                return (
                                    <div
                                        className={classNames(
                                            styles.card,
                                            isInstalled
                                                ? styles.cardInstalled
                                                : styles.cardAvailable,
                                        )}
                                        key={app?.id}
                                    >
                                        <div className={styles.cardHeader}>
                                            <figure className={styles.cardIcon}>
                                                <img
                                                    alt={app?.name}
                                                    height={32}
                                                    src={icon}
                                                    width={32}
                                                />
                                            </figure>
                                            <h4 className={styles.cardTitle}>
                                                {app?.name}
                                            </h4>
                                        </div>
                                        <AppDescription text={description} />
                                        <hr className={styles.cardDivider} />
                                        <div className={styles.cardActions}>
                                            <Button
                                                className={styles.actionButton}
                                                disabled={loading}
                                                loading={showLoading}
                                                onClick={() => {
                                                    handleAction(
                                                        option,
                                                        isInstalled,
                                                    );
                                                }}
                                                size="sm"
                                                type="button"
                                                variant={
                                                    isInstalled
                                                        ? "secondary"
                                                        : "primary"
                                                }
                                            >
                                                {isInstalled
                                                    ? "Uninstall"
                                                    : "Install"}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className={styles.empty}>
                            <Message
                                description={`No results match the criteria${
                                    selectedCategory &&
                                    selectedCategory !== "ALL"
                                        ? ` ( ${selectedCategory} )`
                                        : ""
                                }. Remove criteria to search again`}
                                graphic={<SearchIcon />}
                                title="No search results found"
                            />
                        </div>
                    )}
                </div>
            )}
        </Panel>
    );
};

export default AppsConfigForm;
